/*
 * MappingAdminPage
 *
 * Data Mapping Admin page for managing field mappings
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Footer } from '../../features/footer/Footer';
import { Header } from '../../features/header/Header';
import { Container, Row, Col, CardBody } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { withRequireMappingAdmin } from '../../common/authentication';
import { injectIntl, FormattedMessage } from 'react-intl';
import messages, { TAB_HEADER_MESSAGES } from './messages';
import ModalAlert from '../../common/ModalAlert';
import ApplicationAlert, {
  AlertType,
} from '../../common/domain/ApplicationAlert';
import {
  loadMappingData,
  setActiveTab,
  loadGroups,
  loadMappingDataValues,
  cancelDraftRow,
  clearRowChanges,
  clearEditModeSnapshot,
  setUpdatedOnlyFilter,
  setScreenMode,
  clearSuccessMessage,
} from '../../features/mappingAdmin/mappingAdminSlice';
import { MappingAdminState } from '../../common/domain/mappingAdmin';
import { SCREEN_MODES, TABS, TABS_PATHS } from './constants';
import ViewingAndSelectionScreen from './components/ViewingAndSelectionScreen';
import EditModeScreen from './components/EditModeScreen';
import ConfirmationModal from '../../common/ConfirmationModal';
import { MappingAdminPageProps } from './interfaces';
import AlertsActions from '../../features/alerts/actions';

const MappingAdminPage = ({ intl, authState }: MappingAdminPageProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const unblockRef = useRef<(() => void) | null>(null);

  const { screenMode, activeTab, errorMessage, successMessage } = useSelector(
    (state: any) => state.mappingAdmin as MappingAdminState,
  );

  const alerts = useSelector(
    (state: any) => state.alerts.alerts,
  ) as ApplicationAlert[];

  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [showNavWarning, setShowNavWarning] = useState(false);

  useEffect(() => {
    const tabFromUrl =
      (Object.keys(TABS_PATHS) as Array<keyof typeof TABS_PATHS>).find((tab) =>
        location.pathname.endsWith(TABS_PATHS[tab]),
      ) ?? TABS.RATES_FIELD;
    dispatch(setActiveTab(tabFromUrl));
    dispatch(loadGroups());
    dispatch(loadMappingData(tabFromUrl));
    dispatch(loadMappingDataValues(tabFromUrl));
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (screenMode !== SCREEN_MODES.EDITING_MODE) {
      unblockRef.current?.();
      unblockRef.current = null;
      return;
    }

    unblockRef.current = (history as any).block((nextLocation: any) => {
      setPendingPath(nextLocation.pathname);
      setShowNavWarning(true);
      return false;
    });

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [screenMode, history]);

  useEffect(() => {
    if (errorMessage) {
      const alertsActions = new AlertsActions(dispatch);
      alertsActions.add({
        type: AlertType.ERROR,
        header: 'Error',
        body: errorMessage,
      });
    }
  }, [errorMessage, dispatch]);

  useEffect(() => {
    if (successMessage) {
      const alertsActions = new AlertsActions(dispatch);
      alertsActions.add({
        type: AlertType.INFO,
        header: 'Success',
        body: successMessage,
      });
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch]);

  const handleConfirmNavigation = () => {
    dispatch(cancelDraftRow());
    dispatch(clearRowChanges({ tabType: activeTab }));
    dispatch(clearEditModeSnapshot());
    dispatch(setUpdatedOnlyFilter(false));
    dispatch(setScreenMode(SCREEN_MODES.VIEWING_MODE));
    setShowNavWarning(false);
    unblockRef.current?.();
    unblockRef.current = null;
    if (pendingPath) {
      history.push(pendingPath);
      setPendingPath(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavWarning(false);
    setPendingPath(null);
  };

  const renderScreen = () => {
    const screenProps = {
      username: authState?.username || '',
    };

    switch (screenMode) {
      case SCREEN_MODES.EDITING_MODE:
        return <EditModeScreen {...screenProps} dispatch={dispatch} />;
      case SCREEN_MODES.SELECTING_MODE:
      case SCREEN_MODES.VIEWING_MODE:
      default:
        return (
          <ViewingAndSelectionScreen {...screenProps} dispatch={dispatch} />
        );
    }
  };

  return (
    <>
      <Header />
      <Container fluid>
        <Helmet>
          <title>{intl.formatMessage(messages.title)}</title>
          <meta
            name="description"
            content={intl.formatMessage(messages.metaDesc)}
          />
        </Helmet>

        <Row>
          <Col>
            <h1 className="tpicap-h mb-1 ml-4">
              <FormattedMessage {...TAB_HEADER_MESSAGES[activeTab]} />
            </h1>
          </Col>
        </Row>

        <Row>
          <Col>
            <CardBody>{renderScreen()}</CardBody>
          </Col>
        </Row>
      </Container>
      <Footer />
      <ModalAlert alert={alerts[alerts.length - 1]} />
      <ConfirmationModal
        isOpen={showNavWarning}
        title="Unsaved changes"
        message="You are about to leave this page. All unsaved changes will be lost. Continue?"
        confirmColor="primary"
        loading={false}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
    </>
  );
};

export default withRequireMappingAdmin(injectIntl(MappingAdminPage));
